import { useState, useEffect, useCallback } from 'react';
import { startAutoSync } from '@/lib/syncEngine';
import { getSyncQueue, getDeadLetter, getPhotoQueue, getPhotoDeadLetter } from '@/lib/offlineStorage';
import { toast } from 'sonner';

function getPendingTotal(): number {
  return getSyncQueue().length + getPhotoQueue().length;
}

function getDeadTotal(): number {
  return getDeadLetter().length + getPhotoDeadLetter().length;
}

export function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(getPendingTotal());
  const [deadCount, setDeadCount] = useState(getDeadTotal());

  const refreshPending = useCallback(() => {
    setPendingCount(getPendingTotal());
    setDeadCount(getDeadTotal());
  }, []);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      toast.info('Conexión recuperada. Actualizando tus datos…', { duration: 2500 });
      // Tell every component that listens to refresh its data — this covers
      // the "two devices edited the same record offline" case: when you
      // come back online, the server is the source of truth and the UI
      // refetches whatever the server has now.
      window.dispatchEvent(new CustomEvent('iftmx:reconnected'));
    };
    const goOffline = () => {
      setOnline(false);
      toast.warning('Sin conexión. Sigue usando la app — tus cambios se guardarán localmente.', { duration: 3500 });
    };

    // Toast "guardado, se sincronizará al reconectar" cuando el usuario
    // hace una acción mientras está offline. El evento se dispatch
    // desde offlineMutation y está throttled para no spamear.
    const onMutationQueued = () => {
      toast.info('Guardado. Se sincronizará al recuperar conexión.', {
        id: 'mutation-queued',
        duration: 3000,
      });
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('iftmx:mutation-queued', onMutationQueued);

    const cleanup = startAutoSync((result) => {
      refreshPending();
      setSyncing(false);
      if (result.synced > 0) {
        const word = result.synced === 1 ? 'cambio sincronizado' : 'cambios sincronizados';
        toast.success(`✓ ${result.synced} ${word}`, { duration: 3000 });
      }
      // Common action: open the sync inspector dialog so the user can see
      // EXACTLY what is failing and decide whether to keep, retry or delete.
      const openInspector = () => {
        window.dispatchEvent(new CustomEvent('iftmx:open-sync-dialog'));
      };

      // Each toast has a stable id, so sonner replaces any existing toast
      // of the same kind instead of stacking duplicates when sync runs
      // multiple times in quick succession.
      if (result.needsReAuth) {
        toast.error(
          'Tu sesión expiró',
          {
            id: 'sync-reauth',
            duration: 8000,
            description: 'Vuelve a iniciar sesión para guardar tus cambios pendientes.',
            action: { label: 'Ver qué pasó', onClick: openInspector },
          }
        );
      } else if (result.movedToDead > 0) {
        const word = result.movedToDead === 1 ? 'cambio falló' : 'cambios fallaron';
        toast.error(
          `${result.movedToDead} ${word} al sincronizar`,
          {
            id: 'sync-dead',
            duration: 8000,
            description: 'Necesitamos tu ayuda para resolverlos. Toca para ver qué pasó.',
            action: { label: 'Ver qué pasó', onClick: openInspector },
          }
        );
      } else if (result.failed > 0 && result.synced === 0) {
        const word = result.failed === 1 ? 'cambio aún no se ha sincronizado' : 'cambios aún no se han sincronizado';
        toast.warning(
          `${result.failed} ${word}`,
          {
            id: 'sync-pending',
            duration: 6000,
            description: 'Lo intentamos otra vez automáticamente. Toca para ver detalles.',
            action: { label: 'Ver detalles', onClick: openInspector },
          }
        );
      }
    }, () => {
      setSyncing(true);
    });

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('iftmx:mutation-queued', onMutationQueued);
      cleanup();
    };
  }, [refreshPending]);

  return { online, syncing, pendingCount, deadCount, refreshPending };
}
